/**
 * Urgency Countdown utilities.
 *
 * Display countdown timers to create urgency:
 *   - Flash sale countdown (when to show: listing.publicData.flashSale.endsAt)
 *   - Limited-time offer countdown
 *   - Event start countdown
 *   - Booking hold expiry countdown
 *
 * Returns time remaining broken into days/hours/minutes/seconds.
 */

export const COUNTDOWN_TYPES = ['flash_sale', 'limited_offer', 'event_start', 'booking_hold'];

/**
 * Calculate time remaining until a target date.
 * @param {string|Date} targetDate
 * @param {Date} [now]
 * @returns {{ days, hours, minutes, seconds, totalMs, expired }}
 */
export const getTimeRemaining = (targetDate, now = new Date()) => {
  const target = new Date(targetDate);
  const totalMs = target.getTime() - now.getTime();

  if (totalMs <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0, expired: true };
  }

  const days = Math.floor(totalMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((totalMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((totalMs % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, totalMs, expired: false };
};

/**
 * Format time remaining as a readable string.
 * @param {{ days, hours, minutes, seconds }} remaining
 * @returns {string}
 */
export const formatTimeRemaining = ({ days, hours, minutes, seconds }) => {
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

/**
 * Get urgency label based on time remaining.
 * @param {{ days, expired }} remaining
 * @returns {'high'|'medium'|'low'|'expired'}
 */
export const getUrgencyLevel = ({ days, hours, expired }) => {
  if (expired) return 'expired';
  if (days === 0 && hours < 2) return 'high';    // < 2 hours
  if (days <= 1) return 'medium';                // < 1 day
  return 'low';
};

/**
 * Should countdown be shown based on remaining time.
 * Show only if < 7 days remaining (otherwise not urgent).
 * @param {{ days }} remaining
 * @returns {boolean}
 */
export const shouldShowCountdown = ({ days, expired }) =>
  !expired && days < 7;
